import React from 'react';
import { Card, CardBody } from '@heroui/react';
import { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';

interface SummaryCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: LucideIcon;
  colorClass: string;
  delay?: number;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, icon: Icon, colorClass, delay = 0 }) => {
  const colorMap: Record<string, { bg: string; border: string; text: string; iconBg: string; iconText: string }> = {
    blue: {
      bg: 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-700 dark:text-blue-300',
      iconBg: 'bg-blue-100 dark:bg-blue-900/50',
      iconText: 'text-blue-600 dark:text-blue-400',
    },
    emerald: {
      bg: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30',
      border: 'border-emerald-200 dark:border-emerald-800',
      text: 'text-emerald-700 dark:text-emerald-300',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
      iconText: 'text-emerald-600 dark:text-emerald-400',
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30',
      border: 'border-purple-200 dark:border-purple-800',
      text: 'text-purple-700 dark:text-purple-300',
      iconBg: 'bg-purple-100 dark:bg-purple-900/50',
      iconText: 'text-purple-600 dark:text-purple-400',
    },
    amber: {
      bg: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
      border: 'border-amber-200 dark:border-amber-800',
      text: 'text-amber-700 dark:text-amber-300',
      iconBg: 'bg-amber-100 dark:bg-amber-900/50',
      iconText: 'text-amber-600 dark:text-amber-400',
    },
  };

  const colors = colorMap[colorClass] || colorMap.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
    >
      <Card className={`h-full ${colors.bg} border ${colors.border}`}>
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${colors.text} mb-1`}>{title}</p>
              <p className="text-3xl font-bold text-foreground">{value}</p>
              {subtitle && <p className="text-xs text-default-500 mt-1">{subtitle}</p>}
            </div>
            <div className={`p-3 rounded-full ${colors.iconBg}`}>
              <Icon className={`w-6 h-6 ${colors.iconText}`} />
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default SummaryCard;

